// import { TranscriptMessage } from "@/lib/transcript"
import { Chat, ChatMessage } from "@/lib/vibrato-api"
import { settings } from "@/settings"
import Dropzone from "dropzone"
import { ArrowRight, CheckCircle, ArrowLeft } from "iconoir-react"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import showdown from "showdown"
import { Loading } from "../Loading"
import { VibratoClientContext } from "./contexts"
import ChatInput from "./ChatInput"
import ChatHistory from "./ChatHistory"
import React from "react"

const aiPill = (
  <span className="bg-linear-to-r from-purple-600 to-indigo-600 text-white text-lg font-medium rounded-full flex justify-center items-center h-10 w-10 shadow-md">
    V
  </span>
)

// Function to check if a message contains the confirmation text
const containsConfirmationText = (content: string): boolean => {
  return content.toLowerCase().includes("please confirm that this information is correct".toLowerCase())
}

// Function to extract call edit URL from message content
const extractCallEditUrl = (content: string): string | null => {
  const urlRegex = /\[here\]\(\/calls\/(\d+)\/edit\)/
  const match = content.match(urlRegex)

  if (match) {
    return `/calls/${match[1]}/edit`
  }

  return null
}

export default function ChatComponent(): React.JSX.Element {
  const vibratoClient = useContext(VibratoClientContext)
  const [searchParams] = useSearchParams()
  const { theme } = useParams()
  const navigate = useNavigate()
  const taskDescription = searchParams.get("taskDescription") ? atob(searchParams.get("taskDescription")!) : null

  const otherPartyName = "You"
  const humanPill = useMemo(() => {
    return (
      <span className="bg-blue-800 text-white text-lg font-medium rounded-full dark:bg-blue-900 dark:text-blue-300 flex justify-center items-center h-10 w-10">
        <div>{otherPartyName}</div>
      </span>
    )
  }, [otherPartyName])

  const [chat, setChat] = useState<Chat | null>(null)
  const [messages, setMessages] = useState<Array<ChatMessage>>([])
  const [message, setMessage] = useState("")
  const [typing, setTyping] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [containerHeight, setContainerHeight] = useState(0)

  // New state for chat history
  const [showChatHistory, setShowChatHistory] = useState(false)
  const [chatHistory, setChatHistory] = useState<Chat[]>([])
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false)

  useEffect(() => {
    document.title = "Vibrato - Chat"
  }, [])

  useEffect(() => {
    if (!chat) {
      return
    }

    const myDropzone = new Dropzone("#document-upload", {
      url: () => {
        return `${settings.VIBRATO_URL}/api/chats/${chat.id}/document/`
      },
      paramName: "file",
      maxFilesize: 20,
      clickable: ["#document-upload", "#document-upload-icon"],
      headers: {
        Authorization: `Bearer ${vibratoClient.token}`
      },
      // @ts-ignore
      disablePreviews: true
    })
    // myDropzone.on("sending", () => {
    //   setUploading(true)
    // })
    myDropzone.on("addedfile", (file) => {
      setUploading(true)
      console.log(`File added: ${file.name}`)
    })
    myDropzone.on("success", async (file: any) => {
      setUploading(false)

      console.log(`File uploaded: ${file.name}`)

      // @ts-ignore
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          speaker: "human",
          content: `Uploaded file: ${file.name}`,
          id: Math.random().toString(36).substring(7)
        }
      ])

      try {
        setTyping(true)
        for await (const aiMsg of vibratoClient.createChatResponse(chat.id)) {
          console.log("Received AI message", aiMsg)
          setMessages((prevMessages) => [...prevMessages, aiMsg])
        }
      } catch (err) {
        console.log(err)

        // @ts-ignore
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            speaker: "ai",
            content: "There was an error processing your file. Please try again.",
            id: Math.random().toString(36).substring(7)
          }
        ])
      }

      setTyping(false)
    })
    myDropzone.on("error", (_file: any, message: string) => {
      setUploading(false)
      setTyping(false)

      // @ts-ignore
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          speaker: "ai",
          content: `There was an error processing your file. Please try again.

Error: ${message}`,
          id: Math.random().toString(36).substring(7)
        }
      ])
      console.log(`File failed to upload: ${message}`)
    })

    return () => {
      myDropzone.destroy()
    }
  }, [chat, setTyping, setUploading, setMessages])

  const converter = useMemo(() => {
    return new showdown.Converter()
  }, [])

  const createAndLoad = useCallback(async () => {
    if (showChatHistory) return // Don't create new chat if viewing history

    const chatTheme = theme || "default"

    const chat = await vibratoClient.createChat(chatTheme, taskDescription)
    console.log("Created chat", chat)
    setChat(chat)

    const transcript = await vibratoClient.getChatTranscript(chat.id)
    console.log("Got transcript", transcript)
    setMessages(transcript)

    if (taskDescription) {
      setTyping(true)
      try {
        for await (const aiMsg of vibratoClient.createChatResponse(chat.id)) {
          console.log("Received AI message", aiMsg)
          setMessages((prevMessages) => [...prevMessages, aiMsg])
        }
      } catch (err) {
        console.log(err)
      }
      setTyping(false)
    }
  }, [vibratoClient, setChat, setMessages, theme, taskDescription, showChatHistory])

  useEffect(() => {
    if (!chat && !showChatHistory) {
      createAndLoad()
    }
  }, [createAndLoad, chat, showChatHistory])

  const sendNewMessage = useCallback(
    async (message: any) => {
      if (!chat) {
        return
      }

      const resp = await vibratoClient.createChatHumanMessage(chat.id, message)
      console.log("Sent human message:", message)
      console.log("Received human message:", resp)
      setMessages((prevMessages) => [...prevMessages, resp])
      setMessage("")

      const input = document.getElementById("message-input")
      if (input) {
        // @ts-ignore
        input.value = ""
      }

      setTyping(true)
      try {
        for await (const aiMsg of vibratoClient.createChatResponse(chat.id)) {
          console.log("Received AI message", aiMsg)
          setMessages((prevMessages) => [...prevMessages, aiMsg])
        }
      } catch (err) {
        console.log(err)
      }
      setTyping(false)
    },
    [chat, setMessages, setMessage, setTyping]
  )

  // Function to handle confirmation button click
  const handleConfirmation = useCallback(() => {
    sendNewMessage("I confirm that this information is correct.")
  }, [sendNewMessage])

  // Function to handle call edit button click
  const handleCallEdit = useCallback(
    (url: string) => {
      navigate(url)
    },
    [navigate]
  )

  const loadChatHistory = useCallback(async () => {
    if (!vibratoClient) return

    setChatHistoryLoading(true)
    try {
      const response = await vibratoClient.getChats({ pageSize: 50 })
      setChatHistory(response.results)
    } catch (error) {
      console.error("Error loading chat history:", error)
      setChatHistory([])
    } finally {
      setChatHistoryLoading(false)
    }
  }, [vibratoClient])

  const handleViewHistory = useCallback(() => {
    setShowChatHistory(true)
    loadChatHistory()
  }, [loadChatHistory])

  const handleBackToChat = useCallback(() => {
    setShowChatHistory(false)
  }, [])

  const handleChatSelect = useCallback(
    async (chatId: number) => {
      try {
        const selectedChat = await vibratoClient.getChat(chatId)
        const transcript = await vibratoClient.getChatTranscript(chatId)

        setChat(selectedChat)
        setMessages(transcript)
        setShowChatHistory(false)
        setMessage("")
      } catch (error) {
        console.error("Error loading selected chat:", error)
      }
    },
    [vibratoClient]
  )

  const handleNewChat = useCallback(() => {
    setShowChatHistory(false)
    // Reset current chat to trigger creation of new one
    setChat(null)
    setMessages([])
    setMessage("")
  }, [])

  if (chat == null && !showChatHistory) {
    return <Loading />
  }

  // Check if the last AI message contains the confirmation text
  const lastAiMessage = [...messages].reverse().find((message) => message.speaker === "ai")
  const showConfirmationButton = lastAiMessage && containsConfirmationText(lastAiMessage.content) && !typing

  // Check if the last AI message contains a call edit URL
  const callEditUrl = lastAiMessage ? extractCallEditUrl(lastAiMessage.content) : null
  const showCallEditButton = callEditUrl && !typing

  if (showChatHistory) {
    return (
      <div className="flex flex-col h-full w-full items-center">
        <div className="h-full flex flex-col w-full">
          <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-4">
            <button
              onClick={handleBackToChat}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Chat</span>
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-4xl mx-auto">
              <ChatHistory
                chats={chatHistory}
                loading={chatHistoryLoading}
                onChatSelect={handleChatSelect}
                onNewChat={handleNewChat}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className="flex flex-col h-full w-full items-center"
        style={{
          paddingBottom: "var(--keyboard-height, 0px)"
        }}
      >
        <div className="h-full flex flex-col w-full">
          <div className={`grow w-full h-[calc(100%-${containerHeight}px)] overflow-hidden`}>
            <div className="flex h-full">
              <div id="transcript" className="flex flex-col-reverse w-full rounded h-full">
                <div className="flex flex-col-reverse w-full gap-6 py-6 overflow-auto  px-4 sm:px-8">
                  {/* Confirmation Button */}
                  {showConfirmationButton && (
                    <div className="flex justify-center w-full mb-4">
                      <button
                        onClick={handleConfirmation}
                        className="flex items-center gap-2 px-6 py-3 text-lg font-semibold text-white bg-linear-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 animate-pulse"
                      >
                        <CheckCircle className="w-6 h-6" />
                        Confirm Call Details
                      </button>
                    </div>
                  )}

                  {/* Call Edit Button */}
                  {showCallEditButton && (
                    <div className="flex justify-center w-full mb-4">
                      <button
                        onClick={() => handleCallEdit(callEditUrl!)}
                        className="flex items-center gap-2 px-6 py-3 text-lg font-semibold text-white bg-linear-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 animate-pulse"
                      >
                        <ArrowRight className="w-6 h-6" />
                        Continue to Call Setup
                      </button>
                    </div>
                  )}

                  {typing && (
                    <div className="flex gap-4 animate-pulse place-items-center">
                      <div className="min-w-fit">
                        <div className="w-14">{aiPill}</div>
                      </div>
                      <div className="flex justify-start flex-1">
                        <div className="flex items-center leading-loose bg-purple-50 rounded-2xl px-6 py-4 max-w-[80%]">
                          <p className="text-purple-600 chat-message font-medium flex items-center">Typing...</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {uploading && (
                    <div className="flex flex-row-reverse gap-4 animate-pulse">
                      <div className="min-w-fit">
                        <div className="w-14">{humanPill}</div>
                      </div>
                      <div className="flex justify-end flex-1">
                        <div className="flex items-center leading-loose bg-blue-50 rounded-2xl px-6 py-4 max-w-[80%]">
                          <p className="text-blue-600 chat-message font-medium">Uploading...</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {[...messages].reverse().map((message: any, index) => (
                    <div
                      key={index}
                      className={`flex gap-4 group ${message.speaker === "ai" ? "flex-row" : "flex-row-reverse"}`}
                    >
                      <div className="min-w-fit">
                        <div className="w-14">{message.speaker == "ai" ? aiPill : humanPill}</div>
                      </div>
                      <div
                        className={`flex flex-1 w-full ${message.speaker === "ai" ? "justify-start" : "justify-end"}`}
                      >
                        <div
                          key={message.id}
                          className={`flex items-center leading-loose lg:max-w-[80%] ${
                            message.speaker == "ai" ? "bg-purple-50" : "bg-blue-50"
                          } rounded-2xl px-6 py-4 shadow-sm transition-all duration-200 group-hover:shadow-md`}
                        >
                          {message.speaker == "ai" ? (
                            <div
                              key={message.id}
                              className="text-gray-700 chat-message prose prose-purple max-w-none break-words"
                            >
                              <span dangerouslySetInnerHTML={{ __html: converter.makeHtml(message.content) }} />
                            </div>
                          ) : (
                            <p key={message.id} className="text-gray-700 chat-message whitespace-pre-wrap break-words">
                              {message.content.split("\n").map((line: any, index: any, array: any) => (
                                <span key={index}>
                                  {line}
                                  {index !== array.length - 1 && <br />}
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-transparent">
            <ChatInput
              onHeightChange={setContainerHeight}
              message={message}
              typing={typing}
              uploading={uploading}
              onMessageChange={(e) => setMessage(e.target.value)}
              onSendMessage={() => sendNewMessage(message)}
              onViewHistory={handleViewHistory}
              onKeyDown={(e) => {
                if (typing) {
                  return
                }

                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  sendNewMessage(message)
                }
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}
